import { Star, ExternalLink } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { GoogleReviews } from "@/components/GoogleReviews";
import { VideoTestimonials } from "@/components/VideoTestimonials";
import { PageHero, FinalCTA } from "@/components/shared";
import { business, routes } from "@/lib/site";
import { combine, localBusinessSchema, breadcrumbSchema } from "@/lib/schema";

export default function Testimonials() {
  const jsonLd = combine(
    localBusinessSchema,
    breadcrumbSchema([
      { name: "Home", path: routes.home },
      { name: "Testimonials", path: routes.testimonials },
    ])
  );

  return (
    <>
      <SEO
        path={routes.testimonials}
        title="Patient Testimonials | Dr. Satyam Jaiswal Chiropractor Varanasi"
        description="Read what patients say about chiropractic and pain care with Dr. Satyam Jaiswal in Varanasi. Genuine, patient-focused care with 1,000+ Google reviews."
        jsonLd={jsonLd}
      />
      <PageHero
        eyebrow="Testimonials"
        title="What Our Patients Say"
        sub="Real, patient-focused care — here's what people across Varanasi share about their experience."
        breadcrumb={[{ label: "Testimonials" }]}
      />

      {/* Trust stat band */}
      <section className="bg-white pt-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-4 rounded-2xl bg-soft p-8">
            {business.stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-heading font-extrabold text-2xl sm:text-3xl lg:text-4xl text-navy">{s.value}</div>
                <div className="text-[11px] sm:text-sm text-muted-foreground mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video testimonials carousel */}
      <section className="bg-soft py-16 lg:py-24" data-testid="video-testimonials-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <VideoTestimonials sub="Watch our patients share their experience in their own words." />
        </div>
      </section>

      {/* Live Google Reviews (EmbedSocial) */}
      <section className="bg-white py-16 lg:py-24" data-testid="google-reviews-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-3">
                <Star className="h-3.5 w-3.5 fill-brand-orange" /> Verified Google Reviews
              </span>
              <h2 className="font-heading font-extrabold text-navy text-2xl sm:text-3xl">Real Reviews From Our Patients</h2>
              <p className="mt-2 text-ink max-w-xl">Live reviews pulled directly from our Google Business Profile.</p>
            </div>
            <a
              href={business.socials.google}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-link"
              className="inline-flex items-center gap-2 rounded-full border-2 border-navy/15 px-6 py-3 font-bold text-navy hover:bg-navy hover:text-white transition-colors"
            >
              View on Google <ExternalLink className="h-4 w-4" />
            </a>
          </div>
          <Reveal>
            <GoogleReviews />
          </Reveal>
        </div>
      </section>

      <FinalCTA title="Become Our Next Success Story" />
    </>
  );
}
